/**
 * Beeps are synthesized via the Web Audio API rather than played from an
 * .mp3 asset -- this removes a network/cache dependency for something that
 * must work reliably offline from the very first scan, before the service
 * worker has necessarily cached anything.
 */
let audioContext: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;

  const AudioContextClass = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextClass) return null;

  if (!audioContext) {
    audioContext = new AudioContextClass();
  }

  return audioContext;
}

function playBeep(): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  // Mobile browsers suspend AudioContext until a user gesture resumes it --
  // scanning is itself gesture-initiated (camera permission grant / manual
  // "start scanning" tap), so this resume should always succeed in practice.
  if (ctx.state === 'suspended') {
    void ctx.resume();
  }

  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();

  oscillator.type = 'sine';
  oscillator.frequency.value = 880; // A5 -- a clean, unambiguous "confirm" tone

  const now = ctx.currentTime;
  gain.gain.setValueAtTime(0.001, now);
  gain.gain.exponentialRampToValueAtTime(0.3, now + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);

  oscillator.connect(gain);
  gain.connect(ctx.destination);

  oscillator.start(now);
  oscillator.stop(now + 0.13);
}

function vibrate(): void {
  if ('vibrate' in navigator) {
    try {
      navigator.vibrate(120);
    } catch {
      // Some browsers throw if called outside a user-gesture context or on
      // unsupported hardware -- feedback is a nicety, never fatal to the scan.
    }
  }
}

export const scanFeedback = {
  /** Fires both the audible beep and haptic vibration for a successful scan. */
  success(): void {
    playBeep();
    vibrate();
  }
};
